(function () {
  'use strict';

  /* ======================================================================
     1. PRODUCT CATALOGUE  —  THE ONLY BLOCK YOU NEED TO EDIT
     ----------------------------------------------------------------------
     Replace each object with a real BYOUT product. Field guide:

       name      what appears on screen
       brand     vendor name (shows under the product name)
       handle    Shopify product handle → links to /products/<handle>
       role      cleanser | serum | treatment | moisturizer | spf | exfoliant | eye
       key       the hero ingredient, written as it appears on the INCI list
       skin      which skin types this suits: oily, dry, combination, balanced
       targets   which concerns it addresses (must match CONCERNS ids below)
       safeSensitive   true if it's fine for reactive skin (no fragrance,
                       no high-% acids, no essential oils)
       tier      1 = under PKR 2,000 | 2 = PKR 2,000–5,000 | 3 = PKR 5,000+
       price     display string
       why       the Curator's note. One honest sentence. This is the bit
                 customers screenshot — write it in BYOUT voice.
     ====================================================================== */

  /* ======================================================================
     1. PRODUCT CATALOGUE
     ----------------------------------------------------------------------
     Deliberately empty. Products come from Shopify via the section's
     collection setting, so the quiz can never recommend something that is
     delisted, out of stock, or has stale copy attached to it.

     If nothing comes through, the results page says so rather than
     inventing a routine. See renderResults().

     Set up: sections/byout-quiz.liquid > "Product source collection",
     and the quiz_* metafields on each product.
     ====================================================================== */

  var PRODUCTS = [];

  /* A list metafield arrives as an array. A single line text metafield
     arrives as "oily, combination". Accept either, always return an array.
     Splits on commas or line breaks, since Shopify uses both. */
  function toList(v, fallback) {
    var arr = null;
    if (Array.isArray(v)) arr = v;
    else if (typeof v === 'string' && v.trim()) arr = v.split(/[\n,;]/);
    if (!arr) return fallback;
    arr = arr.map(function (x) { return String(x).trim(); }).filter(Boolean);
    return arr.length ? arr : fallback;
  }

  /* Metafields are typed by hand, so accept the spellings people actually
     use rather than failing silently on one letter. */
  var ROLE_ALIASES = {
    moisturiser: 'moisturizer', moisturizer: 'moisturizer',
    cleanser: 'cleanser', cleanse: 'cleanser', 'face wash': 'cleanser', facewash: 'cleanser',
    serum: 'serum',
    treatment: 'treatment',
    spf: 'spf', sunscreen: 'spf', sunblock: 'spf', suncream: 'spf', sun: 'spf',
    exfoliant: 'exfoliant', exfoliator: 'exfoliant', mask: 'exfoliant',
    eye: 'eye', 'eye cream': 'eye'
  };

  function normRole(v) {
    var k = String(v || '').trim().toLowerCase();
    return ROLE_ALIASES[k] || k;
  }

  /* Target ids are case sensitive in the scoring, and fineLines is the one
     nobody types correctly. Map from lowercase back to the canonical id. */
  var TARGET_IDS = ['acne','texture','pigmentation','dehydration','redness','dullness','fineLines','undereye'];
  var SKIN_IDS = ['oily','dry','combination','balanced'];

  function canon(list, valid) {
    var out = [];
    list.forEach(function (raw) {
      var k = String(raw).trim().toLowerCase().replace(/[\s_-]+/g, '');
      valid.forEach(function (id) {
        if (id.toLowerCase() === k && out.indexOf(id) === -1) out.push(id);
      });
    });
    return out;
  }

  /* If the Liquid section injected a live catalogue from Shopify, use that
     instead of the hard-coded array above. See sections/byout-skin-quiz.liquid. */
  function useLiveCatalogue() {
    var live = window.BYOUT_PRODUCTS;
    if (!live || !live.length) return;
    /* Metafields can be missing on individual products — normalise so one
       half-filled product can't take the whole quiz down. */
    PRODUCTS = live.filter(function (p) { return p && p.role && p.handle; })
      .map(function (p) {
        return {
          name: p.name || '',
          brand: p.brand || '',
          handle: p.handle,
          role: normRole(p.role),
          key: p.key || '',
          skin: canon(toList(p.skin, SKIN_IDS), SKIN_IDS).length
                  ? canon(toList(p.skin, SKIN_IDS), SKIN_IDS) : SKIN_IDS,
          targets: canon(toList(p.targets, []), TARGET_IDS),
          safeSensitive: p.safeSensitive === true,
          tier: p.tier || 1,
          minAge: p.minAge || 0,
          price: p.price || '',
          why: p.why || '',
          url: p.url || ('/products/' + p.handle),
          image: p.image || '',
          variantId: p.variantId || null
        };
      });
  }

  /* ======================================================================
     2. INGREDIENT REFERENCE  (INCI names, kept accurate)
     ====================================================================== */

  var LOOK_FOR = {
    niacinamide:  ['Niacinamide', 'Regulates oil, fades post-acne marks, strengthens the barrier. Tolerated by almost everyone.'],
    salicylic:    ['Salicylic Acid', 'Oil-soluble, so it clears out congested pores rather than just resurfacing the top.'],
    azelaic:      ['Azelaic Acid', 'Works on breakouts, redness and dark marks simultaneously. Unusually gentle for what it does.'],
    ascorbic:     ['Ascorbic Acid', 'Brightens and defends against daytime damage. Needs to be paired with SPF to be worth it.'],
    arbutin:      ['Alpha-Arbutin', 'Interrupts pigment production. Slower than hydroquinone, far safer for long-term use.'],
    tranexamic:   ['Tranexamic Acid', 'Best-in-class for stubborn melasma and heat-triggered pigmentation.'],
    retinal:      ['Retinaldehyde', 'Converts to retinoic acid in one step, so it works faster than retinol at lower irritation.'],
    peptides:     ['Acetyl Hexapeptide-8', 'Supports firmness without the adjustment period actives require.'],
    hyaluronate:  ['Sodium Hyaluronate', 'Pulls water into the upper layers. Needs a moisturiser over it or it evaporates.'],
    glycerin:     ['Glycerin', 'The most reliable humectant there is, and the cheapest. Check it\'s high on the list.'],
    panthenol:    ['Panthenol', 'Calms visible irritation and speeds barrier recovery.'],
    squalane:     ['Squalane', 'Lightweight emollient that seals hydration in without clogging.'],
    ceramide:     ['Ceramide NP', 'Replaces the lipids a compromised barrier has lost.'],
    centella:     ['Centella Asiatica Extract', 'Reduces redness and reactivity. The go-to for skin that\'s been pushed too hard.'],
    lactic:       ['Lactic Acid', 'Exfoliates and hydrates at once. The gentlest entry point into acids.'],
    zinc:         ['Zinc PCA', 'Reduces sebum output and has mild antibacterial activity.'],
    caffeine:     ['Caffeine', 'Constricts vessels temporarily. Genuinely helps puffiness, does nothing for hereditary circles.'],
    zincOxide:    ['Zinc Oxide', 'Broad-spectrum mineral filter. Reactive skin usually tolerates it better than chemical filters.']
  };

  var GO_EASY = {
    fragrance:  ['Parfum / Fragrance', 'The most common cause of contact reactions in skincare. Nothing is lost by avoiding it.'],
    essOils:    ['Essential oils (Limonene, Linalool, Citral)', 'Marketed as natural, still among the most reactive things you can apply.'],
    alcohol:    ['Alcohol Denat.', 'Fine low on an ingredient list. In the top five it will dehydrate you.'],
    strongAHA:  ['High-percentage glycolic acid (10%+)', 'Too aggressive for where your barrier currently is. Build up to it.'],
    scrubs:     ['Physical scrubs with fruit kernels', 'Irregular edges cause micro-tears. A chemical exfoliant does the job cleanly.'],
    heavyOcc:   ['Heavy occlusives (Coconut Oil, Petrolatum-led balms)', 'Comedogenic on skin that\'s already producing plenty of its own oil.'],
    sls:        ['Sodium Lauryl Sulfate', 'Strips lipids that dry skin cannot afford to lose. Look for a milder surfactant.'],
    stacking:   ['Stacking actives in one session', 'Retinoid plus acid plus vitamin C in one night is how barriers break. Alternate nights.'],
    drying:     ['Alcohol-based toners', 'The tight feeling is damage, not cleanliness.']
  };

  /* ======================================================================
     3. QUESTIONS
     ====================================================================== */

  var CONCERNS = [
    { id:'acne',        label:'Breakouts' },
    { id:'pigmentation',label:'Dark spots & uneven tone' },
    { id:'dullness',    label:'Dullness' },
    { id:'texture',     label:'Texture & visible pores' },
    { id:'fineLines',   label:'Fine lines' },
    { id:'dehydration', label:'Dehydration & tightness' },
    { id:'redness',     label:'Redness & irritation' },
    { id:'undereye',    label:'Under-eye circles' }
  ];

  var STEPS = [
    {
      type:'facemap', key:'zones',
      eyebrow:'Mapping',
      q:'Where does your skin misbehave?',
      help:'Tap a zone to mark it. Tap again to cycle through what\u2019s happening there. Leave a zone alone if it behaves.'
    },
    {
      type:'blot', key:'blot',
      eyebrow:'The blot test',
      q:'It\u2019s 2pm. You press a tissue to your T-zone.',
      help:'Drag to match what the tissue looks like when you pull it away. This one question tells us more than any other.'
    },
    {
      type:'cards', key:'postCleanse', eyebrow:'Ten minutes after cleansing',
      q:'You\u2019ve washed your face. Nothing on yet.',
      help:'Ten minutes pass. What\u2019s your skin doing?',
      options:[
        { v:'tight',  t:'Tight and papery',        s:'Feels like it needs something immediately' },
        { v:'fine',   t:'Comfortable',             s:'No tightness, no shine' },
        { v:'shiny',  t:'Already picking up shine',s:'Especially across the nose and forehead' },
        { v:'mixed',  t:'Tight in some places, shiny in others', s:'Cheeks tight, T-zone glossy' }
      ]
    },
    {
      type:'rank', key:'concerns', max:3, eyebrow:'Priorities',
      q:'What are you actually trying to fix?',
      help:'Pick up to three, in the order they bother you. Order matters \u2014 the first one gets the most weight in your routine.',
      options: CONCERNS
    },
    {
      type:'multi', key:'triggers', eyebrow:'Reactivity',
      q:'Has your skin ever thrown a fit?',
      help:'Select everything that\u2019s caused a reaction. If nothing has, say so \u2014 that\u2019s useful information too.',
      options:[
        { v:'fragrance', t:'Fragranced products', s:'Stinging, itching or flushing' },
        { v:'actives',   t:'Retinoids or acids',  s:'Peeling, burning, going red' },
        { v:'sun',       t:'Sun and heat',        s:'Flushes, or pigment darkens' },
        { v:'weather',   t:'Weather changes',     s:'Flares when the season turns' },
        { v:'hormonal',  t:'Hormones doing their thing', s:'Breakouts that arrive on a schedule' },
        { v:'none',      t:'Nothing so far',      s:'It handles most things', exclusive:true }
      ]
    },
    {
      type:'cards', key:'age', eyebrow:'Context',
      q:'How old are you?',
      help:'It changes what\u2019s worth recommending. Retinoids aren\u2019t for everyone, and nobody under twenty needs an anti-ageing serum.',
      options:[
        { v:'under18', t:'Under 18',  s:'Skin is still settling' },
        { v:'18-24',   t:'18 to 24',  s:'Oil and breakouts are the usual story' },
        { v:'25-34',   t:'25 to 34',  s:'Where most people start on prevention' },
        { v:'35-44',   t:'35 to 44',  s:'Texture and tone shift around now' },
        { v:'45plus',  t:'45 and up', s:'Barrier support matters more than actives' }
      ]
    },
    {
      type:'cards', key:'environment', eyebrow:'Environment',
      q:'Where does your skin spend the day?',
      help:'Air conditioning, sun and pollution all change what your skin needs. Especially here.',
      options:[
        { v:'ac',      t:'Air-conditioned indoors', s:'Office or classroom, most of the day' },
        { v:'outside', t:'Out in it',               s:'Sun, traffic, dust, the commute' },
        { v:'home',    t:'Mostly at home',          s:'Limited sun exposure' },
        { v:'split',   t:'A bit of both',           s:'In and out through the day' }
      ]
    },
    {
      type:'cards', key:'effort', eyebrow:'Reality check',
      q:'How many steps will you actually do?',
      help:'Be honest. A three-step routine you follow beats a nine-step routine you abandon in March.',
      options:[
        { v:1, t:'Three, maximum',  s:'And that\u2019s on a good day' },
        { v:2, t:'Around five',     s:'I can commit to that' },
        { v:3, t:'Give me the full thing', s:'I want the steps and the order' }
      ]
    },
    {
      type:'cards', key:'budget', eyebrow:'Budget',
      q:'What\u2019s comfortable, per product?',
      help:'We\u2019d rather recommend three things you\u2019ll buy than eight you won\u2019t.',
      options:[
        { v:1, t:'Under PKR 2,000',    s:'Keep it lean' },
        { v:2, t:'Whatever\u2019s right', s:'Spend where it earns it' }
      ]
    }
  ];

  var ZONES = ['forehead','nose','lcheek','rcheek','chin'];
  var ZONE_STATES = [null, 'oily', 'dry', 'acne'];
  var ZONE_COLOR = { oily:'#C08A3E', dry:'#7FA6C9', acne:'#C4577E' };
  var ZONE_WORD  = { oily:'Oily', dry:'Dry', acne:'Acne' };
  var ZONE_ARIA  = { oily:'oily', dry:'dry or flaking', acne:'breaking out' };

  /* ======================================================================
     4. STATE
     ====================================================================== */

  var S = {
    step: -1,
    zones: { forehead:null, nose:null, lcheek:null, rcheek:null, chin:null },
    blot: 50, blotTouched: false,
    postCleanse: null, concerns: [], triggers: [],
    environment: null, effort: null, budget: null, age: null
  };

  var root  = document.getElementById('by-root');
  var ticks = document.getElementById('by-ticks');

  /* ======================================================================
     5. SCORING
     ====================================================================== */

  function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

  function metrics() {
    // OIL: -100 (parched) .. +100 (very oily)
    var oil = 0;
    if (S.blotTouched) oil += (S.blot - 50) * 0.7;
    if (S.postCleanse === 'tight') oil -= 26;
    if (S.postCleanse === 'shiny') oil += 26;
    if (S.postCleanse === 'mixed') oil += 7;

    var tOily = 0, tDry = 0, cOily = 0, cDry = 0, acneCount = 0;
    ZONES.forEach(function (z) {
      var v = S.zones[z];
      if (!v) return;
      if (v === 'acne') acneCount++;
      var isT = (z === 'forehead' || z === 'nose' || z === 'chin');
      if (v === 'oily') { oil += 6; isT ? tOily++ : cOily++; }
      if (v === 'dry')  { oil -= 6; isT ? tDry++  : cDry++;  }
    });
    oil = clamp(Math.round(oil), -92, 92);

    // SENS: 0..100
    var sens = 0;
    if (S.triggers.indexOf('fragrance') > -1) sens += 28;
    if (S.triggers.indexOf('actives')   > -1) sens += 28;
    if (S.triggers.indexOf('sun')       > -1) sens += 16;
    if (S.triggers.indexOf('weather')   > -1) sens += 16;
    /* hormonal is a cause, not a sensitivity: it doesn't raise the sens score */
    if (S.concerns.indexOf('redness')   > -1) sens += 18;
    ZONES.forEach(function (z) { if (S.zones[z] === 'acne') sens += 3; });
    sens = clamp(Math.round(sens), 0, 100);

    // HYD: 0..100 — dehydration is separate from oiliness
    var hyd = 70;
    if (S.postCleanse === 'tight') hyd -= 19;
    if (S.postCleanse === 'mixed') hyd -= 9;
    if (S.postCleanse === 'fine')  hyd += 6;
    if (S.concerns.indexOf('dehydration') > -1) hyd -= 17;
    if (S.concerns.indexOf('fineLines')   > -1) hyd -= 4;
    if (S.environment === 'ac')      hyd -= 10;
    if (S.environment === 'outside') hyd -= 7;
    hyd -= Math.min(11, (tDry + cDry) * 4);
    if (oil < -30) hyd -= 7;
    hyd = clamp(Math.round(hyd), 6, 96);

    // TYPE
    var combo = (S.postCleanse === 'mixed') || (tOily > 0 && cDry > 0);
    var type;
    if (combo)          type = 'combination';
    else if (oil >  26) type = 'oily';
    else if (oil < -26) type = 'dry';
    else                type = 'balanced';

    var sensBand = sens >= 55 ? 'sensitive' : (sens >= 25 ? 'reactive' : 'resilient');

    return { oil:oil, sens:sens, hyd:hyd, type:type, sensBand:sensBand,
             tOily:tOily, cDry:cDry, acneCount:acneCount };
  }

  /* ======================================================================
     6. LIVE SWATCH  (the signature element)
     ====================================================================== */

  function swatchSVG(id) {
    return ''
    + '<svg class="by-swatch" id="' + id + '" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Live skin reading">'
    + '<defs>'
    +   '<radialGradient id="' + id + '-base" cx="50%" cy="48%" r="72%">'
    +     '<stop offset="0%"  stop-color="#E8CFBD" id="' + id + '-b1"/>'
    +     '<stop offset="76%" stop-color="#E1C6B2" id="' + id + '-b2"/>'
    +     '<stop offset="100%" stop-color="#CDAB98" id="' + id + '-b3"/>'
    +   '</radialGradient>'
    +   '<radialGradient id="' + id + '-sheen" cx="34%" cy="28%" r="34%">'
    +     '<stop offset="0%" stop-color="#FFF7E6" stop-opacity=".95"/>'
    +     '<stop offset="100%" stop-color="#FFF7E6" stop-opacity="0"/>'
    +   '</radialGradient>'
    +   '<radialGradient id="' + id + '-sheen2" cx="70%" cy="66%" r="26%">'
    +     '<stop offset="0%" stop-color="#FFE9BF" stop-opacity=".8"/>'
    +     '<stop offset="100%" stop-color="#FFE9BF" stop-opacity="0"/>'
    +   '</radialGradient>'
    +   '<radialGradient id="' + id + '-bloom" cx="56%" cy="62%" r="52%">'
    +     '<stop offset="0%" stop-color="#C4577E" stop-opacity=".75"/>'
    +     '<stop offset="100%" stop-color="#C4577E" stop-opacity="0"/>'
    +   '</radialGradient>'
    +   '<filter id="' + id + '-grain" x="0%" y="0%" width="100%" height="100%">'
    +     '<feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" seed="7" result="n" id="' + id + '-turb"/>'
    +     '<feColorMatrix in="n" type="saturate" values="0"/>'
    +   '</filter>'
    +   '<clipPath id="' + id + '-clip"><circle cx="100" cy="100" r="78"/></clipPath>'
    + '</defs>'
    + '<circle cx="100" cy="100" r="86" fill="none" stroke="#E7E1E5" stroke-width="1"/>'
    + '<g clip-path="url(#' + id + '-clip)">'
    +   '<circle cx="100" cy="100" r="78" fill="url(#' + id + '-base)"/>'
    +   '<rect x="20" y="20" width="160" height="160" filter="url(#' + id + '-grain)" opacity="0"'
    +     ' id="' + id + '-grainL" style="mix-blend-mode:multiply;transition:opacity .6s ease"/>'
    +   '<circle cx="100" cy="100" r="78" fill="url(#' + id + '-bloom)" opacity="0" id="' + id + '-bloomL" style="transition:opacity .6s ease"/>'
    +   '<circle cx="100" cy="100" r="78" fill="url(#' + id + '-sheen)" opacity="0" id="' + id + '-sheenL" style="transition:opacity .6s ease"/>'
    +   '<circle cx="100" cy="100" r="78" fill="url(#' + id + '-sheen2)" opacity="0" id="' + id + '-sheenL2" style="transition:opacity .6s ease"/>'
    +   '<g id="' + id + '-cracks" opacity="0" stroke="#B08874" stroke-width="0.45" fill="none" stroke-linecap="round" style="transition:opacity .6s ease">'
    +     '<path d="M46 66 L70 78 L62 100 L84 112"/><path d="M118 46 L130 72 L114 88"/>'
    +     '<path d="M146 112 L124 126 L134 148"/><path d="M60 142 L82 136 L92 158"/>'
    +     '<path d="M96 40 L84 60 L58 54"/><path d="M152 84 L134 98 L150 114"/>'
    +     '<path d="M40 104 L58 116 L48 134"/><path d="M104 150 L118 164 L104 178"/>'
    +   '</g>'
    + '</g>'
    // hydration ring — dashed arc, fills clockwise
    + '<circle cx="100" cy="100" r="86" fill="none" stroke="#4E8F92" stroke-width="2.5"'
    +   ' stroke-linecap="round" stroke-dasharray="0 541" transform="rotate(-90 100 100)"'
    +   ' id="' + id + '-ring" style="transition:stroke-dasharray .7s cubic-bezier(.2,.8,.2,1)"/>'
    + '</svg>';
  }

  function paintSwatch(id, m, inert) {
    var q = function (s) { return document.getElementById(id + s); };
    if (!q('-sheenL')) return;

    // Before the first answer there is no reading, so don't fake one.
    if (inert) {
      q('-sheenL').setAttribute('opacity', '0');
      q('-sheenL2').setAttribute('opacity', '0');
      q('-bloomL').setAttribute('opacity', '0');
      q('-cracks').setAttribute('opacity', '0');
      q('-b1').setAttribute('stop-color', '#EDE8EB');
      q('-b2').setAttribute('stop-color', '#E4DDE1');
      q('-b3').setAttribute('stop-color', '#D6CCD2');
      q('-grainL').setAttribute('opacity', '0.06');
      q('-ring').setAttribute('stroke-dasharray', '0 541');
      q('-ring').setAttribute('stroke-opacity', '0');   // no arc means no reading
      return;
    }

    var oilN  = clamp((m.oil + 100) / 200, 0, 1);   // 0..1
    var dryN  = 1 - oilN;

    var gloss = clamp(m.oil / 92, 0, 1);            // matte until oil goes positive
    q('-sheenL').setAttribute('opacity',  (gloss * 0.9).toFixed(2));
    q('-sheenL2').setAttribute('opacity', (Math.max(0, gloss - 0.35) * 1.2).toFixed(2));
    q('-bloomL').setAttribute('opacity',  (m.sens / 100 * 0.85).toFixed(2));
    q('-cracks').setAttribute('opacity',  (clamp((100 - m.hyd) / 100 - 0.28, 0, 1) * 1.15).toFixed(2));

    // base tone: shifts warmer/sallower as oil rises, greyer as hydration drops
    var mid = m.hyd > 55 ? '#DFAF92' : (m.hyd > 30 ? '#D3A188' : '#C0967F');
    q('-b2').setAttribute('stop-color', mid);
    q('-b1').setAttribute('stop-color', m.oil > 20 ? '#FCE7CC' : '#F1D8C6');

    // Surface texture: visible grain rises with congestion, oil and dryness.
    var tex = 0.09 + gloss * 0.13
            + (S.concerns.indexOf('texture') > -1 ? 0.13 : 0)
            + (dryN > 0.68 ? 0.10 : 0);
    q('-grainL').setAttribute('opacity', clamp(tex, 0, 0.4).toFixed(2));
    q('-turb').setAttribute('baseFrequency', (0.62 + dryN * 0.55).toFixed(2));

    // hydration ring
    var C = 2 * Math.PI * 86;
    var lit = C * (m.hyd / 100);
    q('-ring').setAttribute('stroke-opacity', '1');
    q('-ring').setAttribute('stroke-dasharray', lit.toFixed(1) + ' ' + (C - lit).toFixed(1));
  }

  function readoutHTML() {
    return ''
    + '<div class="by-readouts">'
    +   readoutRow('oil',  'Oil')
    +   readoutRow('sens', 'Sens')
    +   readoutRow('hyd',  'Hyd')
    + '</div>';
  }
  function readoutRow(k, label) {
    return '<div class="by-readout by-r-' + k + '">'
      + '<span class="by-readout-key by-mono">' + label + '</span>'
      + '<span class="by-meter"><i id="by-bar-' + k + '"></i></span>'
      + '<span class="by-readout-val" id="by-val-' + k + '">\u2014</span>'
      + '</div>';
  }

  function paintReadouts(m, answered) {
    var set = function (k, txt, leftPct, widthPct) {
      var v = document.getElementById('by-val-' + k), b = document.getElementById('by-bar-' + k);
      if (!v) return;
      v.textContent = txt;
      b.style.left = leftPct + '%';
      b.style.width = widthPct + '%';
    };
    if (!answered) {
      ['oil','sens','hyd'].forEach(function (k) { set(k, '\u2014', 50, 0); });
      return;
    }
    var o = m.oil;
    set('oil', (o > 0 ? '+' : '') + o, o >= 0 ? 50 : 50 + o / 2, Math.abs(o) / 2);
    set('sens', String(m.sens), 0, m.sens);
    set('hyd',  String(m.hyd),  0, m.hyd);
  }

  function swatchCaption(m, answered) {
    if (!answered) return 'Reads live as you answer.';
    var bits = [];
    bits.push(m.oil > 26 ? 'Sebum running high' : m.oil < -26 ? 'Producing very little oil' : 'Oil roughly in range');
    if (m.sens >= 55) bits.push('barrier is reactive');
    else if (m.sens >= 25) bits.push('mildly reactive');
    if (m.hyd < 40) bits.push('water content low');
    return bits.join(', ') + '.';
  }

  function instrumentHTML() {
    return ''
    + '<aside class="by-instrument">'
    +   '<div class="by-swatch-frame">'
    +     '<span class="by-swatch-label by-mono">Live reading</span>'
    +     '<div class="by-swatch-wrap">' + swatchSVG('by-live') + '</div>'
    +     '<div>'
    +       readoutHTML()
    +       '<p class="by-swatch-caption" id="by-caption">Reads live as you answer.</p>'
    +     '</div>'
    +   '</div>'
    + '</aside>';
  }

  function refreshInstrument() {
    var m = metrics();
    var answered = S.blotTouched || S.postCleanse !== null ||
                   ZONES.some(function (z) { return S.zones[z]; });
    paintSwatch('by-live', m, !answered);
    paintReadouts(m, answered);
    var cap = document.getElementById('by-caption');
    if (cap) cap.textContent = swatchCaption(m, answered);
  }

  /* ======================================================================
     7. STEP RENDERERS
     ====================================================================== */

  function paintTicks() {
    if (S.step < 0) { ticks.innerHTML = ''; return; }
    var h = '';
    for (var i = 0; i < STEPS.length; i++) {
      h += '<span class="by-tick ' + (i < S.step ? 'is-done' : i === S.step ? 'is-now' : '') + '"></span>';
    }
    h += '<span class="by-count by-mono">' + String(S.step + 1).padStart(2, '0') + '/'
       + String(STEPS.length).padStart(2, '0') + '</span>';
    ticks.innerHTML = h;
  }

  function faceHTML() {
    // Geometry is deliberately non-overlapping — a zone map you can't read
    // is a zone map people mis-tap.
    var zoneShape = {
      forehead: { cx:100, cy:46,  rx:41, ry:20 },
      nose:     { cx:100, cy:107, rx:17, ry:25 },
      lcheek:   { cx:60,  cy:110, rx:20, ry:25 },
      rcheek:   { cx:140, cy:110, rx:20, ry:25 },
      chin:     { cx:100, cy:172, rx:31, ry:17 }
    };
    var names = { forehead:'Forehead', nose:'T-zone', lcheek:'L cheek', rcheek:'R cheek', chin:'Chin' };

    var g = '';
    ZONES.forEach(function (z) {
      var st = S.zones[z], s = zoneShape[z];
      var col = st ? ZONE_COLOR[st] : '#B7AFB4';
      g += '<g class="by-zone-g" data-zone="' + z + '" role="button" tabindex="0"'
        +  ' aria-label="' + names[z] + ': ' + (st ? ZONE_ARIA[st] : 'not marked') + '">'
        +  '<ellipse class="by-zone" cx="' + s.cx + '" cy="' + s.cy + '" rx="' + s.rx + '" ry="' + s.ry + '"'
        +    ' fill="' + col + '" fill-opacity="' + (st ? '.24' : '.10') + '"'
        +    ' stroke="' + col + '" stroke-width="' + (st ? '1.4' : '.9') + '"'
        +    ' stroke-opacity="' + (st ? '1' : '.55') + '"/>'
        +  '<text class="by-zone-label" x="' + s.cx + '" y="' + (s.cy + (st ? -1 : 2)) + '" text-anchor="middle">'
        +    names[z] + '</text>'
        +  (st ? '<text class="by-zone-state" x="' + s.cx + '" y="' + (s.cy + 9) + '" text-anchor="middle"'
        +        ' fill="' + col + '">' + ZONE_WORD[st] + '</text>' : '')
        +  '</g>';
    });

    var marked = ZONES.filter(function (z) { return S.zones[z]; }).length;

    return ''
    + '<div class="by-face-wrap">'
    +   '<svg class="by-face" viewBox="0 0 200 216" xmlns="http://www.w3.org/2000/svg">'
    +     '<path d="M100 6 C144 6 168 38 168 92 C168 150 138 208 100 208 C62 208 32 150 32 92 C32 38 56 6 100 6 Z" fill="none" stroke="#D9D2D6" stroke-width="1.1"/>'
    +     '<path d="M60 78 C68 70 82 70 90 76" fill="none" stroke="#D9D2D6" stroke-width="1.1"/>'
    +     '<path d="M110 76 C118 70 132 70 140 78" fill="none" stroke="#D9D2D6" stroke-width="1.1"/>'
    +     '<path d="M88 146 C94 142 106 142 112 146 C106 152 94 152 88 146 Z" fill="none" stroke="#D9D2D6" stroke-width="1.1"/>'
    +     g
    +   '</svg>'
    +   '<div>'
    +     '<div class="by-legend">'
    +       legendRow('#C08A3E', 'Oily / shiny')
    +       legendRow('#7FA6C9', 'Dry or flaking')
    +       legendRow('#C4577E', 'Breaking out')
    +     '</div>'
    +     '<p class="by-legend-note">Tap a zone to cycle: oily, dry, breaking out, then clear. '
    +       (marked ? marked + ' of 5 zones marked.' : 'Leave the zones that behave.') + '</p>'
    +   '</div>'
    + '</div>';
  }
  function legendRow(c, t) {
    return '<div class="by-legend-row"><span class="by-legend-dot" style="background:' + c + '"></span>' + t + '</div>';
  }

  function blotHTML() {
    var pct = S.blot;
    var words = pct < 12 ? 'Nothing. Bone dry.'
      : pct < 30 ? 'A faint mark, barely there.'
      : pct < 52 ? 'A light patch where the tissue touched.'
      : pct < 72 ? 'A clear oily patch.'
      : pct < 88 ? 'Translucent in the middle.'
      : 'You can read through it.';
    return ''
    + '<div class="by-slider-block">'
    +   '<div class="by-tissue"><div class="by-tissue-oil" id="by-tissue-oil" style="opacity:' + (pct / 100) + '"></div></div>'
    +   '<input type="range" class="by-slider" id="by-blot" min="0" max="100" value="' + pct + '" aria-label="How oily is the tissue">'
    +   '<div class="by-slider-ends"><span class="by-mono">Clean</span><span class="by-mono">Soaked</span></div>'
    +   '<p class="by-slider-read" id="by-blot-read">' + words + '</p>'
    + '</div>';
  }

  function cardsHTML(step) {
    var cur = S[step.key];
    var h = '<div class="by-options">';
    step.options.forEach(function (o) {
      var on = cur === o.v;
      h += '<button class="by-opt ' + (on ? 'is-on' : '') + '" data-v="' + o.v + '" aria-pressed="' + on + '">'
        +  '<span class="by-opt-body"><span class="by-opt-title">' + o.t + '</span>'
        +  '<span class="by-opt-sub">' + o.s + '</span></span>'
        +  '<span class="by-opt-mark">' + (on ? '\u2713' : '') + '</span></button>';
    });
    return h + '</div>';
  }

  function multiHTML(step) {
    var cur = S[step.key];
    var h = '<div class="by-options by-two">';
    step.options.forEach(function (o) {
      var on = cur.indexOf(o.v) > -1;
      h += '<button class="by-opt ' + (on ? 'is-on' : '') + '" data-v="' + o.v + '" aria-pressed="' + on + '">'
        +  '<span class="by-opt-body"><span class="by-opt-title">' + o.t + '</span>'
        +  '<span class="by-opt-sub">' + o.s + '</span></span>'
        +  '<span class="by-opt-mark">' + (on ? '\u2713' : '') + '</span></button>';
    });
    return h + '</div>';
  }

  function rankHTML(step) {
    var h = '<div class="by-chips">';
    step.options.forEach(function (o) {
      var i = S.concerns.indexOf(o.id);
      var on = i > -1;
      var full = S.concerns.length >= step.max && !on;
      h += '<button class="by-chip ' + (on ? 'is-on' : '') + '" data-v="' + o.id + '"'
        +  (full ? ' disabled' : '') + ' aria-pressed="' + on + '">'
        +  '<span class="by-chip-rank">' + (on ? i + 1 : '') + '</span>' + o.label + '</button>';
    });
    h += '</div>';
    h += '<p class="by-legend-note" style="margin-top:16px">'
      + (S.concerns.length ? 'Ranked: ' + S.concerns.map(function (c, n) {
            return (n + 1) + '. ' + CONCERNS.filter(function (x) { return x.id === c; })[0].label;
          }).join('  \u00B7  ') + '. Tap again to remove.'
        : 'Nothing selected yet.') + '</p>';
    return h;
  }

  function answered(step) {
    if (step.type === 'facemap') return true;                 // "all clear" is valid
    if (step.type === 'blot')    return S.blotTouched;
    if (step.type === 'rank')    return S.concerns.length > 0;
    if (step.type === 'multi')   return S[step.key].length > 0;
    return S[step.key] !== null;
  }

  /* ----------------------------------------------------------------------
     Step transitions. Three things were making this jumpy:
       1. the whole split, instrument included, was rebuilt every step, so the
          instrument restarted its animations mid-quiz;
       2. steps are different heights, so the container snapped between them;
       3. a long page could leave the new question scrolled out of view.
     ---------------------------------------------------------------------- */

  /* Animate the container between two heights instead of letting it snap. */
  function smoothHeight(mutate) {
    var before = root.offsetHeight;
    mutate();
    var after = root.offsetHeight;

    if (!before || !after || Math.abs(after - before) < 2) return;

    root.style.height = before + 'px';
    root.style.overflow = 'hidden';
    void root.offsetHeight;                       // force the start height to stick
    root.style.transition = 'height .3s cubic-bezier(.2,.8,.2,1)';
    root.style.height = after + 'px';

    setTimeout(function () {
      root.style.transition = '';
      root.style.height = '';
      root.style.overflow = '';
    }, 320);
  }

  /* Only scroll if the question has actually gone off the top of the screen. */
  function keepInView() {
    var quiz = document.getElementById('byout-quiz');
    if (!quiz) return;
    var top = quiz.getBoundingClientRect().top;
    if (top >= 0) return;                          // already visible, leave it alone

    var offset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    window.scrollTo({
      top: window.pageYOffset + top - offset - 16,
      behavior: 'smooth'
    });
  }

  function renderStep() {
    var step = STEPS[S.step];
    var body = step.type === 'facemap' ? faceHTML()
             : step.type === 'blot'    ? blotHTML()
             : step.type === 'rank'    ? rankHTML(step)
             : step.type === 'multi'   ? multiHTML(step)
             : cardsHTML(step);

    var paneHTML = ''
    +   '<section class="by-pane by-anim">'
    +     '<span class="by-eyebrow by-mono">' + step.eyebrow + '</span>'
    +     '<h2 class="by-q by-display">' + step.q + '</h2>'
    +     '<p class="by-help">' + step.help + '</p>'
    +     body
    +     '<div class="by-nav">'
    +       '<button class="by-btn by-btn-ghost" id="by-back">Back</button>'
    +       '<div class="by-nav-spacer"></div>'
    +       (step.type === 'facemap' && !ZONES.some(function (z) { return S.zones[z]; })
          ? '<span class="by-nav-hint by-mono">Nothing to flag? Carry on.</span>' : '')
    +       '<button class="by-btn by-btn-go" id="by-next"' + (answered(step) ? '' : ' disabled') + '>'
    +         (S.step === STEPS.length - 1 ? 'Read my skin' : 'Next') + '</button>'
    +     '</div>'
    +   '</section>';

    var split = root.querySelector('.by-split');

    smoothHeight(function () {
      if (split) {
        /* Swap only the question pane. The instrument keeps its DOM, so its
           ring and gradients carry on rather than restarting each step. */
        var oldPane = split.querySelector('.by-pane');
        if (oldPane) {
          oldPane.insertAdjacentHTML('afterend', paneHTML);
          oldPane.parentNode.removeChild(oldPane);
        } else {
          split.insertAdjacentHTML('beforeend', paneHTML);
        }
      } else {
        root.innerHTML = '<div class="by-split">' + instrumentHTML() + paneHTML + '</div>';
      }
    });

    paintTicks();
    refreshInstrument();
    wireStep(step);
    keepInView();
  }

  /* ----------------------------------------------------------------------
     Selecting an option used to call renderStep(), which tore down and
     rebuilt the whole pane - replaying the entrance animation and resizing
     the container on every tap. That was the jumping. Selection now just
     updates the elements already on screen.
     ---------------------------------------------------------------------- */
  function syncStep(step) {
    var mark = function (btn, on) {
      btn.classList.toggle('is-on', !!on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var m = btn.querySelector('.by-opt-mark');
      if (m) m.textContent = on ? '\u2713' : '';
    };

    if (step.type === 'cards') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-opt'), function (b) {
        var raw = b.getAttribute('data-v');
        var val = isNaN(+raw) || raw === '' ? raw : +raw;
        mark(b, S[step.key] === val);
      });
    }

    if (step.type === 'multi') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-opt'), function (b) {
        mark(b, S[step.key].indexOf(b.getAttribute('data-v')) > -1);
      });
    }

    if (step.type === 'rank') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-chip'), function (b) {
        var i = S.concerns.indexOf(b.getAttribute('data-v'));
        var on = i > -1;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (S.concerns.length >= step.max && !on) b.setAttribute('disabled', '');
        else b.removeAttribute('disabled');
        var r = b.querySelector('.by-chip-rank');
        if (r) r.textContent = on ? (i + 1) : '';
      });
      var note = root.querySelector('.by-legend-note');
      if (note) {
        note.textContent = S.concerns.length
          ? 'Ranked: ' + S.concerns.map(function (c, n) {
              return (n + 1) + '. ' + CONCERNS.filter(function (x) { return x.id === c; })[0].label;
            }).join('  \u00B7  ') + '. Tap again to remove.'
          : 'Nothing selected yet.';
      }
    }

    if (step.type === 'facemap') {
      var names = { forehead:'Forehead', nose:'T-zone', lcheek:'L cheek', rcheek:'R cheek', chin:'Chin' };

      Array.prototype.forEach.call(root.querySelectorAll('.by-zone-g'), function (g) {
        var z  = g.getAttribute('data-zone');
        var st = S.zones[z];
        var col = st ? ZONE_COLOR[st] : '#B7AFB4';

        var ell = g.querySelector('.by-zone');
        if (ell) {
          ell.setAttribute('fill', col);
          ell.setAttribute('fill-opacity', st ? '.24' : '.10');
          ell.setAttribute('stroke', col);
          ell.setAttribute('stroke-width', st ? '1.4' : '.9');
          ell.setAttribute('stroke-opacity', st ? '1' : '.55');
        }

        var cy = +(ell ? ell.getAttribute('cy') : 0);
        var label = g.querySelector('.by-zone-label');
        if (label) label.setAttribute('y', cy + (st ? -1 : 2));

        var word = g.querySelector('.by-zone-state');
        if (st) {
          if (!word) {
            word = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            word.setAttribute('class', 'by-zone-state');
            word.setAttribute('x', ell ? ell.getAttribute('cx') : 0);
            word.setAttribute('text-anchor', 'middle');
            g.appendChild(word);
          }
          word.setAttribute('y', cy + 9);
          word.setAttribute('fill', col);
          word.textContent = ZONE_WORD[st];
        } else if (word) {
          word.parentNode.removeChild(word);
        }

        g.setAttribute('aria-label', names[z] + ': ' + (st ? ZONE_ARIA[st] : 'not marked'));
      });

      var marked = ZONES.filter(function (z) { return S.zones[z]; }).length;
      var note = root.querySelector('.by-face-wrap .by-legend-note');
      if (note) {
        note.textContent = 'Tap a zone to cycle: oily, dry, breaking out, then clear. '
          + (marked ? marked + ' of 5 zones marked.' : 'Leave the zones that behave.');
      }
      var hint = root.querySelector('.by-nav-hint');
      if (hint) hint.style.display = marked ? 'none' : '';
    }

    var next = document.getElementById('by-next');
    if (next) next.disabled = !answered(step);

    refreshInstrument();
  }

  function wireStep(step) {
    document.getElementById('by-back').onclick = function () {
      if (S.step === 0) { S.step = -1; renderIntro(); } else { S.step--; renderStep(); }
    };
    document.getElementById('by-next').onclick = function () {
      if (!answered(step)) return;
      if (S.step === STEPS.length - 1) renderProcessing();
      else { S.step++; renderStep(); }
    };

    if (step.type === 'facemap') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-zone-g'), function (g) {
        var cycle = function () {
          var z = g.getAttribute('data-zone');
          var i = ZONE_STATES.indexOf(S.zones[z]);
          S.zones[z] = ZONE_STATES[(i + 1) % ZONE_STATES.length];
          syncStep(step);
        };
        g.onclick = cycle;
        g.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cycle(); } };
      });
    }

    if (step.type === 'blot') {
      var sl = document.getElementById('by-blot');
      var oilLayer = document.getElementById('by-tissue-oil');
      var read = document.getElementById('by-blot-read');
      var upd = function () {
        S.blot = +sl.value; S.blotTouched = true;
        oilLayer.style.opacity = S.blot / 100;
        var p = S.blot;
        read.textContent = p < 12 ? 'Nothing. Bone dry.'
          : p < 30 ? 'A faint mark, barely there.'
          : p < 52 ? 'A light patch where the tissue touched.'
          : p < 72 ? 'A clear oily patch.'
          : p < 88 ? 'Translucent in the middle.'
          : 'You can read through it.';
        document.getElementById('by-next').disabled = false;
        refreshInstrument();
      };
      sl.oninput = upd;
      sl.onchange = upd;
    }

    if (step.type === 'cards') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-opt'), function (b) {
        b.onclick = function () {
          var raw = b.getAttribute('data-v');
          S[step.key] = isNaN(+raw) || raw === '' ? raw : +raw;
          syncStep(step);
        };
      });
    }

    if (step.type === 'multi') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-opt'), function (b) {
        b.onclick = function () {
          var v = b.getAttribute('data-v');
          var arr = S[step.key];
          var opt = step.options.filter(function (o) { return o.v === v; })[0];
          if (opt.exclusive) { S[step.key] = arr.indexOf(v) > -1 ? [] : [v]; }
          else {
            var excl = step.options.filter(function (o) { return o.exclusive; }).map(function (o) { return o.v; });
            arr = arr.filter(function (x) { return excl.indexOf(x) === -1; });
            var i = arr.indexOf(v);
            if (i > -1) arr.splice(i, 1); else arr.push(v);
            S[step.key] = arr;
          }
          syncStep(step);
        };
      });
    }

    if (step.type === 'rank') {
      Array.prototype.forEach.call(root.querySelectorAll('.by-chip'), function (b) {
        b.onclick = function () {
          if (b.hasAttribute('disabled')) return;
          var v = b.getAttribute('data-v');
          var i = S.concerns.indexOf(v);
          if (i > -1) S.concerns.splice(i, 1);
          else if (S.concerns.length < step.max) S.concerns.push(v);
          syncStep(step);
        };
      });
    }
  }

  /* ======================================================================
     8. INTRO
     ====================================================================== */

  function renderIntro() {
    ticks.innerHTML = '';
    root.innerHTML = ''
    + '<div class="by-intro by-anim">'
    +   '<div>'
    +     '<span class="by-eyebrow by-mono">Two minutes \u00B7 ' + STEPS.length + ' questions</span>'
    +     '<h1 class="by-intro-h by-display">Stop guessing<br>what your <em>skin</em> needs.</h1>'
    +     '<p class="by-intro-p">Most skin quizzes ask you to name your skin type and then hand that answer back to you. This one asks where you get shiny, where you get flaky, and what makes your skin react. Then it builds your routine around the ingredients that suit you.</p>'
    +     '<button class="by-btn by-btn-go" id="by-start">Take the quiz</button>'
    +   '</div>'
    +   instrumentHTML()
    + '</div>';
    refreshInstrument();
    document.getElementById('by-start').onclick = function () { S.step = 0; renderStep(); };
  }

  /* ======================================================================
     9. PROCESSING
     ====================================================================== */

  function renderProcessing() {
    ticks.innerHTML = '';
    root.innerHTML = ''
    + '<div class="by-processing by-anim"><div>'
    +   '<div class="by-proc-swatch">' + swatchSVG('by-proc') + '</div>'
    +   '<p class="by-proc-line" id="by-proc-line">Reading your answers</p>'
    + '</div></div>';
    paintSwatch('by-proc', metrics());

    var lines = ['Reading your answers', 'Cross-checking ingredient lists',
                 'Discarding the word \u201Cclean\u201D', 'Matching against the catalogue',
                 'Ordering your routine'];
    var i = 0;
    var el = document.getElementById('by-proc-line');
    var t = setInterval(function () {
      i++;
      if (i >= lines.length) { clearInterval(t); renderResults(); return; }
      if (el) el.textContent = lines[i];
    }, 620);
  }

  /* ======================================================================
     10. RECOMMENDATION ENGINE
     ====================================================================== */

  /* Cleanser, moisturiser and SPF are structural — a routine without them
     isn't a routine. Budget softens their score but never removes them.
     If the only good option is above the stated budget we say so, out loud,
     rather than quietly recommending something worse. */
  var CORE_ROLES = ['cleanser', 'moisturizer', 'spf'];

  function scoreProduct(p, m, relaxSkin) {
    var core = CORE_ROLES.indexOf(p.role) > -1;
    var sc = 3;

    if (p.skin.indexOf(m.type) === -1) {
      if (!relaxSkin) return -1;
      sc -= 4;
    }
    // Never relaxed: a reactive barrier is not worth a better keyword match.
    if (m.sensBand === 'sensitive' && !p.safeSensitive) return -1;

    /* Age gate. Nothing with a minAge goes to a under-18, and anti-ageing
       is noise at that age even when it's technically safe. */
    if (S.age === 'under18') {
      if ((p.minAge || 0) > 0) return -1;
      if (p.targets.indexOf('fineLines') > -1) sc -= 3;
    }
    if (S.age === '18-24' && p.targets.indexOf('fineLines') > -1) sc -= 1;

    // If they told us this is their number one problem, we show them the thing
    // that solves it and flag the price, rather than pretending it doesn't exist.
    var solvesTopConcern = S.concerns.length > 0 && p.targets.indexOf(S.concerns[0]) > -1;
    if (p.tier > S.budget) {
      if (!core && !solvesTopConcern) return -1;
      sc -= 2.5;
    }

    S.concerns.forEach(function (c, i) {
      if (p.targets.indexOf(c) > -1) sc += (3 - i) * 2.5;
    });
    if (m.hyd < 42 && p.targets.indexOf('dehydration') > -1) sc += 2;
    if (m.sensBand !== 'resilient' && p.safeSensitive) sc += 1.5;
    if (p.tier === S.budget) sc += 0.5;
    if (p.tier < S.budget) sc += 0.25;
    return sc;
  }

  function pick(role, m, used) {
    var attempt = function (relax) {
      var best = null, bestScore = -Infinity;
      PRODUCTS.forEach(function (p) {
        if (p.role !== role || used.indexOf(p.handle) > -1) return;
        var s = scoreProduct(p, m, relax);
        if (s > 0 && s > bestScore) { bestScore = s; best = p; }
      });
      return best;
    };
    var best = attempt(false) || attempt(true);   // widen skin-type match if nothing fits
    if (best) used.push(best.handle);
    return best;
  }

  function overBudgetNote(p) {
    if (!p || p.tier <= S.budget) return '';
    return 'This sits above the range you gave us. We\u2019re still recommending it \u2014 it\u2019s the one place we\u2019d push you.';
  }

  function buildRoutine(m) {
    var used = [];
    var am = [], pm = [];

    // Work out which slots exist first, so we never consume a product
    // for a step the routine won't actually show.
    var wantSerumAM = S.effort >= 2;
    // Eye product earns a slot on a full routine, or when it's a stated priority.
    var eyeIdx      = S.concerns.indexOf('undereye');
    var wantEye     = S.effort >= 3 || (eyeIdx > -1 && eyeIdx < 2);
    var wantExfol   = S.effort >= 2;

    var cleanser = pick('cleanser', m, used);
    var moist    = pick('moisturizer', m, used);
    var spf      = pick('spf', m, used);
    var treatPM  = pick('treatment', m, used);
    var serumAM  = wantSerumAM ? pick('serum', m, used) : null;
    if (!treatPM) treatPM = pick('serum', m, used);           // fall back only if needed
    if (!serumAM && !treatPM) treatPM = pick('serum', m, used);
    var exfol    = wantExfol ? pick('exfoliant', m, used) : null;
    var eye      = wantEye   ? pick('eye', m, used) : null;

    if (cleanser) am.push(['Cleanse', cleanser, overBudgetNote(cleanser)]);
    if (serumAM)  am.push(['Treat', serumAM, overBudgetNote(serumAM)]);
    if (eye)      am.push(['Eyes', eye, overBudgetNote(eye)]);
    if (moist)    am.push(['Moisturise', moist, overBudgetNote(moist)]);
    if (spf)      am.push(['Protect', spf, 'Every day, indoors included. ' + (overBudgetNote(spf) ||
                    'If you skip one thing on this page, don\u2019t let it be this one.')]);

    if (cleanser) pm.push(['Cleanse', cleanser, '']);
    if (exfol)    pm.push(['Exfoliate', exfol, 'Two nights a week to start. Never the same night as your treatment.']);
    if (treatPM)  pm.push(['Treat', treatPM, exfol ? 'Alternate nights with your exfoliant.' : overBudgetNote(treatPM)]);
    if (eye)      pm.push(['Eyes', eye, '']);
    if (moist)    pm.push(['Moisturise', moist, '']);

    return { am: am, pm: pm };
  }

  function buildINCI(m) {
    var yes = [], no = [];
    var add = function (arr, k) { if (arr.indexOf(k) === -1) arr.push(k); };

    S.concerns.forEach(function (c) {
      if (c === 'acne')         { add(yes,'salicylic'); add(yes,'niacinamide'); add(yes,'azelaic'); }
      if (c === 'pigmentation') { add(yes,'ascorbic'); add(yes,'arbutin'); add(yes,'tranexamic'); }
      if (c === 'dullness')     { add(yes,'ascorbic'); add(yes,'lactic'); }
      if (c === 'texture')      { add(yes,'salicylic'); add(yes,'retinal'); add(yes,'niacinamide'); }
      if (c === 'fineLines')    { add(yes,'retinal'); add(yes,'peptides'); }
      if (c === 'dehydration')  { add(yes,'hyaluronate'); add(yes,'glycerin'); add(yes,'squalane'); }
      if (c === 'redness')      { add(yes,'centella'); add(yes,'panthenol'); add(yes,'azelaic'); }
      if (c === 'undereye')     { add(yes,'caffeine'); add(yes,'peptides'); }
    });
    if (m.type === 'oily' || m.type === 'combination') { add(yes,'niacinamide'); add(yes,'zinc'); }
    if (m.type === 'dry')  { add(yes,'ceramide'); add(yes,'squalane'); }
    if (m.hyd < 45) { add(yes,'glycerin'); add(yes,'panthenol'); }
    if (m.sensBand !== 'resilient') { add(yes,'centella'); add(yes,'panthenol'); }
    if (S.environment === 'outside') { add(yes,'zincOxide'); add(yes,'ascorbic'); }

    if (m.sensBand !== 'resilient') { add(no,'fragrance'); add(no,'essOils'); add(no,'strongAHA'); add(no,'scrubs'); }
    if (m.sensBand === 'resilient') { add(no,'fragrance'); add(no,'stacking'); }
    if (m.type === 'oily' || m.type === 'combination') add(no,'heavyOcc');
    if (m.type === 'dry' || m.hyd < 45) { add(no,'sls'); add(no,'alcohol'); add(no,'drying'); }
    if (S.triggers.indexOf('actives') > -1) add(no,'stacking');

    // Never leave the columns looking thin — top up with the fundamentals
    // that apply to everyone before trimming.
    ['glycerin', 'niacinamide', 'panthenol', 'squalane'].forEach(function (k) {
      if (yes.length < 4) add(yes, k);
    });
    if (no.length < 3) { add(no, 'stacking'); add(no, 'drying'); }

    return { yes: yes.slice(0, 6), no: no.slice(0, 5) };
  }

  function verdictCopy(m) {
    var t = { oily:'Oily', dry:'Dry', combination:'Combination', balanced:'Balanced' }[m.type];
    var sensWord = { sensitive:'Sensitive', reactive:'Mildly reactive', resilient:'Resilient' }[m.sensBand];
    var head = t + (m.type === 'combination' && m.oil > 15 ? ', leaning oily' : m.type === 'combination' && m.oil < -15 ? ', leaning dry' : '');

    var p = [];
    if (m.type === 'combination') p.push('Your T-zone and your cheeks want different things, which is why one product keeps half-working.');
    if (m.type === 'oily')        p.push('You make a lot of oil. Stripping it feels right and makes it worse, because your skin just makes more.');
    if (m.type === 'dry')         p.push('You don\u2019t make much oil, so anything foaming or alcohol-heavy takes away what little you have.');
    if (m.type === 'balanced')    p.push('Oil levels are steady. This is about keeping it that way, not fixing anything.');

    if (m.hyd < 40) p.push('You\u2019re also dehydrated, which is a water problem, not an oil one. Skin can be both at once.');
    if (m.sensBand === 'sensitive') p.push('Your barrier is reactive, so this calms first and fixes second. One new thing at a time, a week apart.');
    else if (m.sensBand === 'reactive') p.push('You react to some things, so we kept the fragrance and the strong stuff out.');
    if (S.environment === 'outside') p.push('You\u2019re outside a lot, so sunscreen matters more here than any serum does.');
    if (S.environment === 'ac') p.push('Air conditioning dries you out all day, so hydration matters more for you than most.');
    if (S.triggers.indexOf('hormonal') > -1) p.push('If your breakouts turn up on a schedule, products manage them rather than fix them. Worth asking a doctor, and we\u2019d rather say that than sell you a fourth serum.');
    if (S.age === 'under18') p.push('Your skin is still settling, so we left the strong stuff out. Gentle and regular beats harsh and occasional.');

    return { head: head, sensWord: sensWord, body: p.join(' ') };
  }

  /* Roles, in words people use. */
  var ROLE_WORD = {
    'Cleanse':'Wash', 'Treat':'Treat', 'Eyes':'Eyes',
    'Moisturise':'Moisturise', 'Protect':'SPF', 'Exfoliate':'Exfoliate'
  };

  var ICON_SUN = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4.6" fill="currentColor" opacity=".28"/><circle cx="12" cy="12" r="4.6" fill="none" stroke="currentColor" stroke-width="1.6"/><g stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M12 2.4v2.4M12 19.2v2.4M2.4 12h2.4M19.2 12h2.4M5.2 5.2l1.7 1.7M17.1 17.1l1.7 1.7M18.8 5.2l-1.7 1.7M6.9 17.1l-1.7 1.7"/></g></svg>';
  var ICON_MOON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 14.2A8.4 8.4 0 0 1 9.8 4 8.8 8.8 0 1 0 20 14.2Z" fill="currentColor" opacity=".28"/><path d="M20 14.2A8.4 8.4 0 0 1 9.8 4 8.8 8.8 0 1 0 20 14.2Z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

  function slotHTML(n, role, p, note, repeat) {
    var meta = repeat
      ? '<span class="by-again">already in your list</span>'
      : '<span class="by-slot-price">' + p.price + '</span>'
        + '<a class="by-slot-link" href="' + (p.url || ('/products/' + p.handle)) + '">See it</a>';
    var why = repeat ? 'Same one as the morning.' : (p.why + (note ? ' ' + note : ''));
    return ''
    + '<div class="by-slot">'
    +   '<span class="by-slot-n">' + String(n).padStart(2, '0') + '</span>'
    +   '<div>'
    +     '<span class="by-slot-role">' + (ROLE_WORD[role] || role) + '</span>'
    +     '<div class="by-slot-name">' + p.name + '</div>'
    +     '<p class="by-slot-why">' + why + '</p>'
    +     '<div class="by-slot-meta">' + meta + '</div>'
    +   '</div>'
    + '</div>';
  }

  function renderResults() {
    var m = metrics();
    var v = verdictCopy(m);
    var r = buildRoutine(m);
    var inci = buildINCI(m);

    var badges = ['Oil ' + (m.oil > 0 ? '+' : '') + m.oil, 'Sensitivity ' + m.sens, 'Hydration ' + m.hyd];
    S.concerns.forEach(function (c, i) {
      badges.push('Priority ' + (i + 1) + ': ' + CONCERNS.filter(function (x) { return x.id === c; })[0].label);
    });

    var hasProducts = r.am.length > 0 || r.pm.length > 0;

    var h = '<div class="by-anim">';

    h += '<div class="by-verdict">'
      +    '<span class="by-eyebrow by-mono">Here\u2019s your read</span>'
      +    '<h2 class="by-verdict-h by-display">' + v.head + ', and <em>' + v.sensWord.toLowerCase() + '.</em></h2>'
      +    '<p class="by-verdict-p">' + v.body + '</p>'
      +    '<div class="by-badges">' + badges.map(function (b) {
              return '<span class="by-badge">' + b + '</span>'; }).join('') + '</div>'
      +  '</div>';

    // routine
    var seen = [];
    var renderCol = function (rows, icon, label) {
      var out = '<div><div class="by-when">' + icon
        + '<b>' + label + '</b><i>' + rows.length + ' step' + (rows.length === 1 ? '' : 's') + '</i></div>';
      rows.forEach(function (s, i) {
        var handle = s[1].handle;
        var repeat = seen.indexOf(handle) > -1;
        if (!repeat) seen.push(handle);
        out += slotHTML(i + 1, s[0], s[1], s[2], repeat);
      });
      return out + '</div>';
    };

    if (hasProducts) {
      h += '<section class="by-sec">'
        +  '<div class="by-routine">'
        +    renderCol(r.am, ICON_SUN, 'AM')
        +    renderCol(r.pm, ICON_MOON, 'PM')
        +  '</div></section>';
    } else {
      h += '<section class="by-sec by-empty">'
        +  '<p class="by-empty-h by-display">We can\u2019t build your routine right now.</p>'
        +  '<p class="by-empty-p">Your read above is still accurate, and so is the ingredient list below. '
        +  'Take that to any shelf, here or anywhere else. We\u2019ll have the matching products back shortly.</p>'
        +  '</section>';
    }

    // ingredients — kept, but after the routine and much shorter
    h += '<section class="by-sec by-sec--inci">'
      +  '<h3 class="by-sec-t by-display">What to look for on a label</h3>'
      +  '<p class="by-sec-d">Names are marketing. The ingredient list is the truth. Screenshot this.</p>'
      +  '<div class="by-inci-cols">'
      +    '<div class="by-inci-col"><div class="by-inci-head"><span class="by-mono">Look for</span></div><div class="by-inci-list">'
      +      inci.yes.map(function (k) { return '<div><div class="by-inci-name">' + LOOK_FOR[k][0] + '</div></div>'; }).join('')
      +    '</div></div>'
      +    '<div class="by-inci-col"><div class="by-inci-head"><span class="by-mono">Go easy on</span></div><div class="by-inci-list">'
      +      inci.no.map(function (k) { return '<div><div class="by-inci-name">' + GO_EASY[k][0] + '</div></div>'; }).join('')
      +    '</div></div>'
      +  '</div></section>';

    /* Cart button only appears when the catalogue carries real Shopify
       variant IDs, i.e. when the section is reading a live collection. */
    var cartItems = [];
    r.am.concat(r.pm).forEach(function (s) {
      var p = s[1];
      if (p && p.variantId && cartItems.indexOf(p.variantId) === -1) cartItems.push(p.variantId);
    });

    if (hasProducts) {
      h += '<p class="by-onething">If you only do one thing: <b>wear the sunscreen.</b> It will do more than the rest combined.</p>';
    }

    h += '<div class="by-result-foot">'
      +  (cartItems.length ? '<button class="by-btn by-btn-go" id="by-cart">Add them all to cart</button>' : '')
      +  '<button class="by-btn by-btn-ghost" id="by-copy">Save my read</button>'
      +  '<button class="by-btn by-btn-ghost" id="by-restart">Do it again</button>'
      +  '</div>';

    h += '</div>';
    root.innerHTML = h;

    if (cartItems.length) {
      document.getElementById('by-cart').onclick = function (e) {
        var btn = e.target;
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems.map(function (id) { return { id: id, quantity: 1 }; }) })
        })
        .then(function (res) { if (!res.ok) throw new Error('add failed'); return res.json(); })
        .then(function () { window.location.href = '/cart'; })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = 'Couldn\u2019t add \u2014 try again';
        });
      };
    }

    document.getElementById('by-restart').onclick = function () {
      S.step = -1; S.zones = { forehead:null, nose:null, lcheek:null, rcheek:null, chin:null };
      S.blot = 50; S.blotTouched = false; S.postCleanse = null; S.concerns = [];
      S.triggers = []; S.environment = null; S.effort = null; S.budget = null; S.age = null;
      renderIntro();
    };

    document.getElementById('by-copy').onclick = function (e) {
      var txt = 'BYOUT — The Skin Read\n\n' + v.head + '. ' + v.sensWord + '.\n'
        + 'Oil ' + m.oil + ' / Sensitivity ' + m.sens + ' / Hydration ' + m.hyd + '\n\n'
        + 'LOOK FOR: ' + inci.yes.map(function (k) { return LOOK_FOR[k][0]; }).join(', ') + '\n'
        + 'GO EASY ON: ' + inci.no.map(function (k) { return GO_EASY[k][0]; }).join(', ') + '\n\n'
        + (hasProducts
            ? 'AM: ' + r.am.map(function (s) { return s[1].name; }).join(' → ') + '\n'
              + 'PM: ' + r.pm.map(function (s) { return s[1].name; }).join(' → ')
            : '');
      try { navigator.clipboard.writeText(txt); e.target.textContent = 'Copied'; }
      catch (err) { e.target.textContent = 'Select and copy manually'; }
      setTimeout(function () { e.target.textContent = 'Save my read'; }, 2200);
    };

    /* The email block was removed from the results markup but this handler
       was left behind, throwing on every results render. Guarded. */
    var sendBtn = document.getElementById('by-send');
    if (sendBtn) sendBtn.onclick = function () {
      var el = document.getElementById('by-email');
      var fine = document.getElementById('by-fine');
      var btn = document.getElementById('by-send');

      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(el.value)) {
        fine.textContent = 'That email address is missing something. Check it and try again.';
        fine.style.color = 'var(--sens)';
        el.focus();
        return;
      }

      /* Everything the quiz knows about this person, in one object.
         Passed to your own handler if you set one (see below), and used
         to tag the customer record in Shopify. */
      var payload = {
        email: el.value,
        skin_type: m.type,
        sensitivity: m.sensBand,
        oil_score: m.oil,
        hydration_score: m.hyd,
        concerns: S.concerns,
        budget_tier: S.budget,
        recommended: r.am.concat(r.pm).map(function (s) { return s[1].handle; })
      };

      btn.disabled = true;
      btn.textContent = 'Saving\u2026';
      fine.style.color = '';

      function done(ok) {
        if (ok) {
          btn.textContent = 'Saved';
          fine.textContent = 'You\u2019re on the list. Use Copy my results below to keep your read now.';
        } else {
          btn.disabled = false;
          btn.textContent = 'Try again';
          fine.textContent = 'That didn\u2019t go through. Check your connection and try once more.';
          fine.style.color = 'var(--sens)';
        }
      }

      /* --------------------------------------------------------------
         OPTION A (default): Shopify's own customer list.
         Creates / subscribes the customer and tags them with their skin
         profile so you can segment in Shopify Email. No app needed.

         OPTION B: your own handler. Define window.BYOUT_ON_SUBMIT before
         this script runs (Klaviyo, Mailchimp, a webhook, anything) and it
         will be used instead. It receives the payload above and must
         return a Promise.
         -------------------------------------------------------------- */
      if (typeof window.BYOUT_ON_SUBMIT === 'function') {
        Promise.resolve(window.BYOUT_ON_SUBMIT(payload))
          .then(function () { done(true); })
          .catch(function () { done(false); });
        return;
      }

      var tags = [
        'skin-quiz',
        'skin-' + payload.skin_type,
        'sensitivity-' + String(payload.sensitivity).toLowerCase().replace(/[^a-z0-9]+/g, '-')
      ].concat((payload.concerns || []).slice(0, 3).map(function (c) { return 'concern-' + c; }));

      var body = 'form_type=customer&utf8=%E2%9C%93'
        + '&contact%5Btags%5D=' + encodeURIComponent(tags.join(','))
        + '&contact%5Bemail%5D=' + encodeURIComponent(el.value);

      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body
      })
      .then(function (res) { done(res.ok); })
      .catch(function () { done(false); });
    };
  }

  /* ======================================================================
     BOOTSTRAP — safe to call more than once (theme editor re-renders the
     section without reloading the page, which fires shopify:section:load).
     ====================================================================== */
  function init() {
    if (!document.getElementById('by-root')) return;
    useLiveCatalogue();
    renderIntro();
  }
  window.BYOUTQuiz = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#by-root')) init();
  });
})();